import React, { useEffect, useMemo, useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import { TASK_TITLE_CHAR_LIMIT } from "@lifeat/shared/task-management/TaskManagementConstants";
import { Mutex, withMutex } from "@lifeat/shared/util/AsyncUtil";
import { Result } from "@lifeat/shared/util/Result";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import buildCompositeKey from "@util/ReactUtil";
import { CompleteTaskEvent } from "../AmplitudeTaskManagementEvents";
import { useTaskListContext } from "../TaskListContext";
import { SingularTaskItemProps } from "./SingularTaskItem";
import { StyledTaskTitleWrapper, TaskItemContainer, TaskStatusCheckbox } from "./TaskItemComponents";
import {
    TASK_TITLE_INPUT_DEBOUNCE_DELAY_MS,
    TASK_TITLE_INPUT_MAX_HEIGHT,
    TASK_TITLE_INPUT_MIN_HEIGHT,
    TASK_TITLE_INPUT_PLACEHOLDER,
} from "./TaskItemConstants";

const TaskElementColumn = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    gap: 6px;
`;

export interface DraftTaskItemProps extends Omit<SingularTaskItemProps, "index"> {
    /**
     * Callback to create a new db task based on the draft task
     */
    readonly handleCreateTask: (updatedTitle: string) => Promise<Result>;
}
export default function DraftTaskItem(props: DraftTaskItemProps) {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const amplitudeService = AmplitudeService.getInstance();
    const { updateTaskStatus, deleteDraftTask, updateDraftTaskTitle } = useTaskListContext();

    const isTaskCreated = useRef<boolean | null>(null);
    const isTaskCompleted = props.task.status === "COMPLETE";

    useEffect(() => {
        if (props.isTaskInFocus && inputRef?.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(inputRef.current.value.length || 0, inputRef.current.value.length || 0);
        }
    }, [props.isTaskInFocus]);

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement, Element>) => {
        if (e.target.value.trim() === "") {
            deleteDraftTask(props.taskId);
        }
    };

    const mutex = useMemo(() => new Mutex(), []);
    const handleCreateTask = withMutex(mutex, async (updatedTitle: string) => {
        if (isTaskCreated.current) {
            return;
        }
        const result = await props.handleCreateTask(updatedTitle);
        if (result.success) {
            isTaskCreated.current = true;
        }
    });

    const [inputVal, setInputValAndDebounce] = useDebouncedInput(
        props.task.title,
        (updatedTitle: string) => handleCreateTask(updatedTitle),
        TASK_TITLE_INPUT_DEBOUNCE_DELAY_MS,
    );

    const handleUpdateTitle = (updatedTitle: string) => {
        // On title update, immediately the draftTaskItems state
        // so that it always has the latest task title
        // (This is to allow updating the title after the task has been created)
        updateDraftTaskTitle(props.taskId, updatedTitle);
        setInputValAndDebounce(updatedTitle);
    };

    return (
        <TaskItemContainer isTouchDevice={isMobile || isTablet}>
            <TaskStatusCheckbox
                key={buildCompositeKey(`task-status-checkbox`, props.taskId, isTaskCompleted)}
                isTaskCompleted={isTaskCompleted}
                isDisabled={true}
                updateTaskStatus={(status) => updateTaskStatus(props.taskId, status)}
                onComplete={() => {
                    amplitudeService.logEvent(
                        new CompleteTaskEvent({
                            type: "singular",
                            source: props.amplitudeEventSource,
                            clientId: props.clientId,
                        }),
                    );
                }}
            />
            <TaskElementColumn>
                <StyledTaskTitleWrapper isCompleted={isTaskCompleted}>
                    <LifeAtMultiLineTextInput
                        textValue={inputVal}
                        placeholder={TASK_TITLE_INPUT_PLACEHOLDER}
                        onInputChange={handleUpdateTitle}
                        onBlur={handleBlur}
                        minHeightPx={TASK_TITLE_INPUT_MIN_HEIGHT}
                        maxHeightPx={TASK_TITLE_INPUT_MAX_HEIGHT}
                        handleKeyPress={props.handleKeyPress}
                        maxCharacterCount={TASK_TITLE_CHAR_LIMIT}
                        inputRef={inputRef}
                    />
                </StyledTaskTitleWrapper>
            </TaskElementColumn>
        </TaskItemContainer>
    );
}
